import { __rest, __assign } from 'tslib';
import React, { useRef, useState, cloneElement } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { useShouldRender } from '../../utils/useShouldRender.esm.js';
import { Button } from '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/FaIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { TruncateText } from '../TruncateText/TruncateText.esm.js';
import { Body } from '../Typography/Body.esm.js';
import { Dropdown } from './Dropdown.esm.js';
import { selectedItemLabel, isDomElement } from './utils.esm.js';

/**
 * This component is like `Select` but always has searching enabled.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectWithSearch } from "@panwds/react-ui";
 * ```
 */
var SelectWithSearch = function (_a) {
    var _b, _c;
    var dataAutofocus = _a["data-autofocus"], button = _a.button, buttonRef = _a.buttonRef, _d = _a.fullWidth, fullWidth = _d === void 0 ? false : _d, selectedItem = _a.selectedItem, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, disabled = _a.disabled, _e = _a.defaultPlacement, defaultPlacement = _e === void 0 ? "bottom-start" : _e, darkMode = _a.darkMode, placeholder = _a.placeholder, customWidth = _a.customWidth, _f = _a.fitToItemWidth, fitToItemWidth = _f === void 0 ? false : _f, rest = __rest(_a, ["data-autofocus", "button", "buttonRef", "fullWidth", "selectedItem", "dataMetrics", "dataTestId", "disabled", "defaultPlacement", "darkMode", "placeholder", "customWidth", "fitToItemWidth"]);
    var anchorRef = useRef(null);
    var _g = usePopper(__assign({ placement: defaultPlacement, strategy: "fixed" }, (fitToItemWidth && {
        modifiers: [
            {
                name: "sameWidth",
                enabled: true,
                fn: function (_a) {
                    var state = _a.state;
                    state.styles.popper.width = "".concat(state.rects.reference.width, "px");
                },
                phase: "beforeWrite",
                requires: ["computeStyles"]
            },
        ]
    }))), triggerRef = _g.triggerRef, containerRef = _g.containerRef;
    var _h = useState(false), isOpen = _h[0], setIsOpen = _h[1];
    var shouldRender = useShouldRender(isOpen);
    var DefaultButton = (React.createElement(Button, { "data-autofocus": dataAutofocus, "data-testid": "select-trigger", fullWidth: fullWidth, isMenu: true, isOpen: isOpen },
        React.createElement(TruncateText, { addClassName: "tw-flex-auto" }, selectedItemLabel(selectedItem) ||
            (placeholder ? (React.createElement(Body, { appearance: "secondary", addClassName: "tw-text-xs" }, placeholder)) : ("")))));
    var addProps = isDomElement(button === null || button === void 0 ? void 0 : button.type.toString()) ? {} : { isOpen: isOpen };
    var trigger = cloneElement(button || DefaultButton, __assign(__assign(__assign(__assign({}, addProps), { id: rest.toggleButtonId, "aria-labelledby": "".concat((_b = rest.labelId) !== null && _b !== void 0 ? _b : "", " ").concat((_c = rest.toggleButtonId) !== null && _c !== void 0 ? _c : ""), "aria-expanded": isOpen, "aria-haspopup": "listbox", ref: mergeRefs([buttonRef, anchorRef, triggerRef]), onMouseUp: function () { return setIsOpen(!isOpen); }, onKeyDown: function (event) {
            if (event.key === "Enter" || event.key === " ") {
                setIsOpen(true);
            }
        } }), (customWidth && { style: { width: customWidth } })), { disabled: disabled }));
    return (React.createElement("div", __assign({ className: classNames(darkMode && "dark") }, (customWidth && { style: { width: customWidth } })),
        trigger,
        shouldRender && (React.createElement(Dropdown, __assign({}, rest, { dataMetrics: dataMetrics, dataTestId: dataTestId, darkMode: darkMode, isOpen: isOpen, selectedItem: selectedItem, anchorRef: anchorRef, containerRef: containerRef, onOpenChange: setIsOpen, fullWidth: fullWidth })))));
};

export { SelectWithSearch };
//# sourceMappingURL=SelectWithSearch.esm.js.map
